import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GlobalNotification, NonAuthRoutes } from '../../model'
import { Resources } from '../../model/RBAC/constants'

import { DataTableSkeleton, UserPermission } from '@globaleris/ui.library'
import { hasPermission } from '../../helpers/permissionsHelper'
import { MapToArray } from '../../helpers/arrayHelper/arrayHelper'
import { enumEnumToValueNumber, enumToMapValues } from '../../helpers/enumHelper'

import {
  getActiveCommodity,
  storeActiveCommodity,
  storeActiveContract,
  storeActiveFlexibility,
  storeActiveTabForCurrentPage,
  storeCommodityFilterForCurrentPage
} from '../../redux'
import { usePageState } from '../../hooks/usePageState'

import PageHeader from '../../components/page/pageHeader'
import Page from '../../components/page/page'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'

import { Commodity, FlexibilityTab, FlexibilityTableModel } from './flexibility.Model'
import { EnumCommodity, EnumTipoCalculoConcepto } from '../../api'

import {
  CalculationConceptType,
  FlexibilitiesModel
} from '../../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { fetchCalculationConceptTypes } from './flexibility.service'
import { calculationConceptTypesToFlexibilityTabListMapper } from './flexibility.mapper'
import { FlexibilitiesTable } from '../../components/shared/flexibilitiesTable/flexibilitiesTable'
import useFlexibilities from '../../components/shared/flexibilitiesTable/flexibilitiesTable.hooks'
import { useHistory } from 'react-router'
import { EnumSubtiposFlexibilidad } from '../../api/oData/enums/enumSubtiposFlexibilidad'

export interface FlexibilityPageProps {}

export const FlexibilityPage: React.FC<FlexibilityPageProps> = props => {
  const [t] = useTranslation(['flexibility', 'globalerisLibrary', 'enums'])
  const history = useHistory()

  const initialCommodityFilter: EnumCommodity = getActiveCommodity() ?? EnumCommodity.GasNatural

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )

  const {
    dataTableFilter,
    dataTableSearch,
    activeTab: activeTabStore,
    commodityFilter
  } = usePageState()

  const [calculationTypes, setCalculationTypes] = useState<CalculationConceptType[] | null>(null)
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [isFetchingTabs, setIsFetchingTabs] = useState<boolean>(true)

  // Initial Active Tab
  const storeActiveTab = activeTabStore ?? 0

  const [commodity, setCommodity] = useState<Commodity>({
    activeCommodity: commodityFilter || initialCommodityFilter
  })

  const flexibilityTabsArray: FlexibilityTab[] = calculationTypes
    ? calculationConceptTypesToFlexibilityTabListMapper(calculationTypes)
    : MapToArray(enumToMapValues(EnumTipoCalculoConcepto))

  const { flexibilities, isFetchingFlexibilities, refreshFlexibilities } = useFlexibilities({
    commodity: commodity.activeCommodity,
    calculationType: storeActiveTab != null ? flexibilityTabsArray[storeActiveTab].value : undefined
  })

  // We set an Array from the EnumTipoCalculoConcepto in order to have arranged both index (table tabs index, starting in 0) and values corresponding to each CalculationType.

  const tabsDefinition: Map<any, Tab> = new Map(
    flexibilityTabsArray.map((calculationType, tabIndex) => [
      tabIndex,
      {
        label: calculationType.name,
        hasPermission: hasPermission(Resources.Flexibility, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ])
  )

  const fetchConceptTypes = async () => {
    setIsFetchingTabs(true)
    const concepts = await fetchCalculationConceptTypes()
      .then(response => {
        if (response.success && response.data) {
          return response.data ?? []
        }
        // If API fails, return null and will show CalculationType enum tabs
        return null
      })
      .finally(() => setIsFetchingTabs(false))
    setCalculationTypes(concepts)
  }

  // Hooks Region Starts
  React.useEffect(() => {
    storeActiveContract(null)
    fetchConceptTypes()
  }, [])
  // Hooks Region Ends

  // Handlers Region Starts

  const onSelectCommodityHandler = (commodity: EnumCommodity): void => {
    setCommodity({ activeCommodity: commodity })
    storeCommodityFilterForCurrentPage(commodity)
    storeActiveCommodity(commodity)
  }

  const onTableFetching = (childIsFetching: boolean) => {
    setIsFetching(childIsFetching)
  }

  const handleViewAdditionals = (flexibility: FlexibilitiesModel) => {
    storeActiveFlexibility(flexibility)
    history.push(NonAuthRoutes.PriceComponents)
  }

  const handleClickViewPriceComponents = (flexibility: FlexibilityTableModel) => {
    const flex = flexibilities?.find(item => item.id === flexibility.id)

    flex && handleViewAdditionals(flex)
  }

  // Handlers Region Ends

  // Functions Region Starts

  const renderTabContent = (activeTab: number) => {
    return flexibilityTabsArray[activeTab] ? (
      <FlexibilitiesTable
        flexibilities={flexibilities}
        isFetchingFlexibilities={isFetchingFlexibilities}
        refreshFlexibilities={refreshFlexibilities}
        dataTableFilter={dataTableFilter}
        dataTableSearch={dataTableSearch}
        onClickViewPriceComponents={handleClickViewPriceComponents}
        // onFetching={onTableFetching}
        // onGlobalNotificationChange={onGlobalNotificationChangeHandler}
      ></FlexibilitiesTable>
    ) : null
  }

  // Functions Region Ends

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('flexibility:title')}>
        <CommodityFilter
          commodities={[
            { commodity: EnumCommodity.GasNatural },
            { commodity: EnumCommodity.Electricidad }
          ]}
          commodity={commodity.activeCommodity}
          enabled={!isFetching}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      </PageHeader>
      {!isFetchingTabs ? (
        <>
          <TabsWithPermissions
            tabs={tabsDefinition}
            tabDefault={storeActiveTab}
            onTabChange={index => {
              // if (currentTab !== index) {
              //   setCurrentTab(index)
              storeActiveTabForCurrentPage(index)
              setGlobalNotification(undefined)
              // }
            }}
          ></TabsWithPermissions>
          {renderTabContent(storeActiveTab)}
        </>
      ) : (
        <DataTableSkeleton />
      )}
    </Page>
  )
}
